/**
 * ProcessPicker — grid of manufacturing modules.
 *
 * PR 15 of the modularisation roadmap. Renders the customer-facing
 * landing picker that lets the user choose what to make (laser cut,
 * 3D print, bend, ...). Available modules link to ``/services/{slug}``;
 * unavailable modules render with a "Brzy" badge but stay visible
 * for marketing + SEO purposes.
 *
 * The component is registry-driven via ``useProcesses`` — adding a
 * new module on the backend lights up a new card here automatically.
 *
 * Status: PR 15. Wires the standalone component. PR 16 swaps it
 * into the Landing page.
 */

import { Card } from "@/ingot";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  pickMaterialCategoryLabel,
  useMaterialCategories,
  type MaterialCategory,
} from "@/api/queries/materialCategories";
import { useProcesses } from "@/api/queries/processes";

import { OperationIcon } from "@/components/cart/OperationIcon";
import { Pill } from "@/components/ui/Pill";
import { cx } from "@/lib/utils";
import { hasConfigurator, sortByLandingOrder } from "@/processes/registry";
import type { Process } from "@/api/types";

/**
 * localStorage key for the customer's last-clicked module slug.
 * Read on mount, used to bubble that module to the head of the
 * grid + render a small "Naposledy" hint above its card. Plan §
 * "Moment 1" calls for this as the mitigation for the extra click
 * the picker introduces over the legacy direct-upload flow.
 */
const LAST_MODULE_KEY = "ps.lastModule";

export function readLastModuleSlug(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(LAST_MODULE_KEY);
  } catch {
    return null;
  }
}

export function rememberLastModuleSlug(slug: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LAST_MODULE_KEY, slug);
  } catch {
    // localStorage disabled (Safari private mode); silent no-op.
  }
}

/**
 * Material categories a process belongs to (1:N). Falls back to the
 * singular ``material_category`` for back-compat with payloads/fixtures
 * that don't carry the list. Kept in one place so the chip-visibility
 * derivation and the active-category filter always see the same set.
 */
function catsOf(p: Process): string[] {
  return p.material_categories?.length
    ? p.material_categories
    : [p.material_category];
}

// --- Category tree — one-way downward inheritance -----------------------
//
// Categories form a tree via ``parent_slug``. A process tagged to a parent is
// available under every descendant chip; a process tagged only to a child
// never leaks up to the parent. We resolve that by ancestor-inclusive
// matching: a process belongs under chip ``C`` iff one of its tags is ``C`` or
// an ancestor of ``C``. Mirrors ``ancestors_of`` in the backend service.

function buildCategoryMaps(categories: MaterialCategory[]): {
  parentBySlug: Map<string, string | null>;
  childrenBySlug: Map<string, MaterialCategory[]>;
  roots: MaterialCategory[];
} {
  const known = new Set(categories.map((c) => c.slug));
  const parentBySlug = new Map<string, string | null>();
  for (const c of categories) {
    // An inactive/missing parent is dropped from the public catalog, which
    // severs inheritance from it — treat such a node as a root.
    const parent =
      c.parent_slug && known.has(c.parent_slug) ? c.parent_slug : null;
    parentBySlug.set(c.slug, parent);
  }
  const childrenBySlug = new Map<string, MaterialCategory[]>();
  for (const c of categories) {
    const parent = parentBySlug.get(c.slug) ?? null;
    if (parent === null) continue;
    const arr = childrenBySlug.get(parent) ?? [];
    arr.push(c);
    childrenBySlug.set(parent, arr);
  }
  const roots = categories.filter(
    (c) => (parentBySlug.get(c.slug) ?? null) === null,
  );
  return { parentBySlug, childrenBySlug, roots };
}

/** ``[slug, ...ancestors]`` (visited-guarded against corrupt cycles). */
function ancestorsInclusive(
  slug: string,
  parentBySlug: Map<string, string | null>,
): string[] {
  const out = [slug];
  const visited = new Set([slug]);
  let cur = parentBySlug.get(slug) ?? null;
  while (cur !== null && !visited.has(cur)) {
    out.push(cur);
    visited.add(cur);
    cur = parentBySlug.get(cur) ?? null;
  }
  return out;
}

/** Every slug in the subtree rooted at ``slug`` (inclusive). */
function subtreeInclusive(
  slug: string,
  childrenBySlug: Map<string, MaterialCategory[]>,
): Set<string> {
  const out = new Set([slug]);
  const stack = [slug];
  while (stack.length) {
    const cur = stack.pop() as string;
    for (const child of childrenBySlug.get(cur) ?? []) {
      if (!out.has(child.slug)) {
        out.add(child.slug);
        stack.push(child.slug);
      }
    }
  }
  return out;
}

interface ProcessPickerProps {
  /** Locale for the URLs ProcessPicker emits — ``/cs`` / ``/en``. */
  lang: string;
  /** Optional: include chain-only modules (finishes). False by
   *  default since the picker is for primary processes. */
  includeNonEntry?: boolean;
  /** Override for storybook / tests. When provided, skips the
   *  network and renders directly. */
  processesOverride?: Process[];
  /** Override for storybook / tests. When provided, skips the
   *  ``useMaterialCategories`` network fetch. */
  materialCategoriesOverride?: MaterialCategory[];
}

export function ProcessPicker({
  lang,
  includeNonEntry = false,
  processesOverride,
  materialCategoriesOverride,
}: ProcessPickerProps): JSX.Element {
  const { i18n } = useTranslation();
  const query = useProcesses({
    includeNonEntry,
    enabled: processesOverride === undefined,
  });
  const processes = processesOverride ?? query.data ?? [];

  const materialCategoriesQuery = useMaterialCategories({
    enabled: materialCategoriesOverride === undefined,
  });
  const materialCategories =
    materialCategoriesOverride ?? materialCategoriesQuery.data ?? [];

  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [lastSlug, setLastSlug] = useState<string | null>(null);

  // Read once on mount — localStorage isn't reactive across tabs and
  // the picker reload-cycle is short enough not to matter.
  useEffect(() => {
    setLastSlug(readLastModuleSlug());
  }, []);

  const { parentBySlug, childrenBySlug, roots } = useMemo(
    () => buildCategoryMaps(materialCategories),
    [materialCategories],
  );

  // Slugs with at least one *renderable* process directly tagged — the same
  // ``hasConfigurator`` gate the grid uses (below), so a chip never appears
  // for a category whose only processes can't be ordered (which is what
  // produced the "chips shown / grid empty" mismatch).
  const present = useMemo(
    () => new Set(processes.filter((p) => hasConfigurator(p)).flatMap(catsOf)),
    [processes],
  );

  const filtered = useMemo(() => {
    // Only show modules that have a working configurator — either a
    // hand-coded FE component or a schema-driven DynamicConfigurator
    // (sandbox modules). Without one there's no working ordering flow,
    // so a "Naše služby" tile would dead-end on ComingSoon. Surface
    // finishes fall out here too (per-part modifier, not standalone).
    let list = sortByLandingOrder(processes).filter((p) =>
      hasConfigurator(p),
    );
    if (activeCategory) {
      // Downward inheritance: a process matches the active chip if any of its
      // tags is the chip itself OR an ancestor of it.
      const chain = ancestorsInclusive(activeCategory, parentBySlug);
      list = list.filter((p) => catsOf(p).some((c) => chain.includes(c)));
    }
    if (search.trim()) {
      const needle = search.trim().toLowerCase();
      list = list.filter(
        (p) =>
          p.display_name.toLowerCase().includes(needle) ||
          p.description.toLowerCase().includes(needle) ||
          p.accepted_file_types.some((ext) =>
            ext.toLowerCase().includes(needle),
          ),
      );
    }
    // Bubble the customer's last-clicked module to the top so the
    // "one extra click" cost of the picker disappears for repeat
    // visitors. Only when no category / search filter is active —
    // otherwise the user explicitly asked for a different slice.
    if (lastSlug && !activeCategory && !search.trim()) {
      const idx = list.findIndex((p) => p.slug === lastSlug && p.is_available);
      if (idx > 0) {
        const [hit] = list.splice(idx, 1);
        list.unshift(hit);
      }
    }
    return list;
  }, [processes, search, activeCategory, lastSlug, parentBySlug]);

  if (query.isLoading && processesOverride === undefined) {
    return (
      <div
        className="text-sm text-ink-3"
        data-testid="process-picker-loading"
      >
        Načítám služby…
      </div>
    );
  }

  // --- Drill-down chip navigation -------------------------------------
  // A chip is worth showing if a renderable process is available at the node
  // or anywhere in its subtree (tagged to it, an ancestor, or a descendant).
  const nodeUsable = (slug: string): boolean => {
    for (const a of ancestorsInclusive(slug, parentBySlug)) {
      if (present.has(a)) return true;
    }
    for (const d of subtreeInclusive(slug, childrenBySlug)) {
      if (present.has(d)) return true;
    }
    return false;
  };
  const sortCats = (arr: MaterialCategory[]): MaterialCategory[] =>
    [...arr].sort((a, b) => a.sort_order - b.sort_order);
  // Path from the root down to the active node — drives which child rows show.
  const activePath = activeCategory
    ? ancestorsInclusive(activeCategory, parentBySlug).slice().reverse()
    : [];
  // One chip row per level: usable roots, then the usable children of each
  // node on the active path. Arbitrary depth.
  const levels: MaterialCategory[][] = [
    sortCats(roots.filter((r) => nodeUsable(r.slug))),
  ];
  for (const pathSlug of activePath) {
    const kids = sortCats(
      (childrenBySlug.get(pathSlug) ?? []).filter((c) => nodeUsable(c.slug)),
    );
    if (kids.length > 0) levels.push(kids);
  }
  // Show the filter when there's more than one usable category to choose from.
  const showCategoryFilter =
    materialCategories.filter((c) => nodeUsable(c.slug)).length > 1;

  return (
    <section data-testid="process-picker" className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Hledat službu nebo formát"
          aria-label="Hledat službu"
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm shadow-sm focus:border-border-strong focus:outline-none sm:w-72"
          data-testid="process-picker-search"
        />
        {showCategoryFilter && (
          <div
            className="flex flex-col gap-2"
            role="group"
            aria-label="Filtr kategorií"
          >
            {levels.map((levelCats, li) => (
              <div key={li} className="flex flex-wrap gap-2">
                {li === 0 && (
                  <button
                    type="button"
                    onClick={() => setActiveCategory(null)}
                    aria-pressed={activeCategory === null}
                    className={cx(
                      "rounded-md border px-3 py-1.5 text-xs font-medium transition",
                      activeCategory === null
                        ? "border-border-strong bg-ink text-bg"
                        : "border-border bg-surface text-ink-2 hover:bg-surface-2",
                    )}
                  >
                    Vše
                  </button>
                )}
                {levelCats.map((category) => {
                  // The chip on the active path at this level is highlighted;
                  // clicking it again steps back up to its parent.
                  const isActive = activePath[li] === category.slug;
                  return (
                    <button
                      key={category.slug}
                      type="button"
                      onClick={() =>
                        setActiveCategory(
                          isActive
                            ? (parentBySlug.get(category.slug) ?? null)
                            : category.slug,
                        )
                      }
                      aria-pressed={isActive}
                      className={cx(
                        "rounded-md border px-3 py-1.5 text-xs font-medium transition",
                        isActive
                          ? "border-border-strong bg-ink text-bg"
                          : "border-border bg-surface text-ink-2 hover:bg-surface-2",
                      )}
                    >
                      {pickMaterialCategoryLabel(category, i18n.language)}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      {filtered.length === 0 ? (
        <p
          className="text-sm text-ink-3"
          data-testid="process-picker-empty"
        >
          Žádná služba neodpovídá filtrům.
        </p>
      ) : (
        <div
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
          data-testid="process-picker-grid"
        >
          {filtered.map((process, idx) => (
            <ProcessCard
              key={process.name}
              process={process}
              lang={lang}
              lastSlug={lastSlug}
              isFirst={idx === 0}
            />
          ))}
        </div>
      )}
    </section>
  );
}

interface ProcessCardProps {
  process: Process;
  lang: string;
  lastSlug: string | null;
  isFirst: boolean;
}

function ProcessCard({
  process,
  lang,
  lastSlug,
  isFirst,
}: ProcessCardProps): JSX.Element {
  const href = `/${lang}/services/${process.slug}`;
  const isLast = lastSlug === process.slug && process.is_available;

  const card = (
    <Card
      elevation={process.is_available ? "raised" : "flat"}
      className={cx(
        "st-proc h-full transition",
        process.is_available
          ? "hover:border-border-strong hover:shadow-lg"
          : "opacity-70",
        isLast && isFirst && "ring-2 ring-border-strong",
      )}
      data-testid={`process-card-${process.slug}`}
      data-available={process.is_available}
    >
      {/* Icon tile of the storefront themes (KAN-1546); the original look
          has none, so it stays hidden unless a theme shows it. */}
      <span aria-hidden="true" className="st-proc-ic hidden">
        <OperationIcon
          iconUrl={process.icon_url}
          iconKey={process.icon_key}
          slug={process.slug}
          displayName={process.display_name}
          size={24}
        />
      </span>
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-base font-semibold text-ink">
          {process.display_name}
        </h3>
        {!process.is_available && <Pill tone="warn">Brzy</Pill>}
        {isLast && isFirst && (
          <Pill tone="accent" data-testid="process-card-last">
            Naposledy
          </Pill>
        )}
      </div>
      <p className="mt-2 text-sm text-ink-2">{process.description}</p>
    </Card>
  );

  if (!process.is_available) {
    return <div aria-disabled="true">{card}</div>;
  }

  return (
    <Link
      to={href}
      onClick={() => rememberLastModuleSlug(process.slug)}
      className="block focus:outline-none"
    >
      {card}
    </Link>
  );
}
